import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AsyncActionIcon, Button, ContainerHeader, ContainerLoading, ErrorResponse } from '~components';
import { faFileLines, faFilePdf } from '~components/icon';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { useDocumentBatchGeneration } from '~main/components/document-batch-jobs/useDocumentBatchGeneration';

import { DOCUMENT_TYPES } from '~constants/common';

import { useGetCompletedQuery } from '~forms/actions/completedForms';

import { FormField } from '~forms/types';
import { resolveTokens } from '~forms/lib/resolvedFieldValue';

import { RichTextView } from './rich-text';

/**
 * The captured signature from a frozen snapshot, with a fallback for records that carry none.
 *
 * The prefix check upstream proves the value claims to be a PNG data URI, not that its bytes decode
 * to one. Rather than decoding on every render to prove it, let the browser be the decoder: if the
 * image fails to load for any reason (a truncated, corrupt, or mislabelled payload), fall back to the
 * same message a never-captured signature shows, instead of leaving a broken image in a compliance
 * record.
 */
const SignatureSnapshot: React.FC<{ hasSignature: boolean; src: string; alt: string }> = ({
  hasSignature,
  src,
  alt,
}) => {
  const [failed, setFailed] = useState(false);

  return (
    <div className="form-preview-signature">
      {hasSignature && !failed ? (
        <img src={src} alt={alt} onError={() => setFailed(true)} />
      ) : (
        <div className="form-preview-static">No signature captured</div>
      )}
    </div>
  );
};

const CompletedFormReadOnly: React.FC = () => {
  // completed-form id is a UUID — read it from the route (AppDynamicRoute only uses the param as a
  // remount key, it does NOT pass it as a prop).
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { generateSeparate, isGenerating } = useDocumentBatchGeneration();
  const { showToast } = useToast();

  const handleGeneratePdf = async () => {
    if (!id) return;
    try {
      await generateSeparate(DOCUMENT_TYPES.CUSTOM_FORM.SINGLE, [id]).unwrap();
      showToast('success', 'Form PDF generation started');
    } catch (err) {
      showToast('danger', err instanceof Error ? err.message : 'Failed to generate PDF');
    }
  };

  const {
    data,
    isLoading,
    isError,
    error,
  } = useGetCompletedQuery(id ?? '', { skip: !id });

  if (!id) return <ErrorResponse message="No completed form ID provided" />;
  if (isLoading) return <ContainerLoading loading />;
  if (isError) return <ErrorResponse message={getErrorMessage(error)} />;
  if (!data) return <ErrorResponse message="Completed form not found" />;
  if (!data.version?.field_schema) return <ErrorResponse message="Form schema not available" />;

  const { fields } = data.version.field_schema;
  const { field_responses: fieldResponses = {} } = data;

  // The completed form is a FROZEN SNAPSHOT — render entirely from `field_responses`, with NO live
  // re-resolve. Field values are stored resolved; labels/help that embedded a merge token were frozen
  // under `label:<id>` / `help:<id>` at submit time. Plain labels come straight from the frozen schema.
  // Legacy degradation path: completed forms submitted before TAV-2661 have no `label:`/`help:` snapshot
  // keys. For those we fall back to the live schema label resolved against an EMPTY map — so any
  // {{merge:...}} token in a legacy label renders blank rather than throwing. This is intentional: new
  // records always carry the frozen value, and we never re-resolve at view time (the snapshot is the truth).
  const frozenLabel = (field: FormField) => fieldResponses[`label:${field.id}`] ?? resolveTokens(field.label || '', {});
  const frozenHelp = (field: FormField) => fieldResponses[`help:${field.id}`] ?? resolveTokens(field.help_text || '', {});

  const renderField = (field: FormField) => {
    if (field.type === 'section_divider') {
      return (
        <h5 key={field.id} className="form-preview-section">
          {fieldResponses[field.id] || 'Section'}
        </h5>
      );
    }

    if (field.type === 'rich_text') {
      // Rendered from the frozen snapshot value (tokens already resolved at submit time), NOT re-resolved.
      return (
        <div key={field.id} className="form-preview-rich-text">
          <RichTextView html={fieldResponses[field.id] ?? ''} registry={[]} />
        </div>
      );
    }

    if (field.type === 'merge_field') {
      const snapshotValue = fieldResponses[field.id] ?? '';
      const labelText = frozenLabel(field);
      return (
        <div key={field.id} className="form-preview-field">
          {labelText && <label className="form-preview-label">{labelText}</label>}
          <div className="form-preview-static">{snapshotValue || <span className="text-muted">—</span>}</div>
        </div>
      );
    }

    if (field.type === 'signature') {
      const labelText = frozenLabel(field);
      const snapshotValue = fieldResponses[field.id] ?? '';
      // Frozen record: render the captured PNG straight from the snapshot. Only a validated
      // `data:image/png;base64,` value renders as an image. Anything else (never captured, or a
      // legacy/non-PNG value) falls back to the static "no signature" message rather than a broken <img>.
      const hasSignature = /^data:image\/png;base64,/.test(snapshotValue);
      return (
        <div key={field.id} className="form-preview-field">
          {labelText && <label className="form-preview-label">{labelText}</label>}
          {field.help_text && <div className="form-preview-help">{frozenHelp(field)}</div>}
          <SignatureSnapshot
            hasSignature={hasSignature}
            src={snapshotValue}
            alt={labelText || 'Signature'}
          />
        </div>
      );
    }

    if (field.type === 'checkbox') {
      // Snapshot stores 'true'/'false' — render human-readable (and note 'false' is a truthy string,
      // so it must not fall through to the generic `value || —` branch).
      return (
        <div key={field.id} className="form-preview-field">
          <label className="form-preview-label">{frozenLabel(field)}</label>
          {field.help_text && <div className="form-preview-help">{frozenHelp(field)}</div>}
          <div className="form-preview-static">{fieldResponses[field.id] === 'true' ? 'Yes' : 'No'}</div>
        </div>
      );
    }

    // For all other fields: show the submitted value from the frozen snapshot (covers both editable
    // answers and read-only fields, which are now snapshotted at submit time).
    const isRequired = field.required && !field.read_only;
    const submittedAnswer = fieldResponses[field.id] ?? '';

    return (
      <div key={field.id} className="form-preview-field">
        <label className="form-preview-label">
          {frozenLabel(field)}
          {isRequired && <span className="text-danger ms-1" aria-hidden="true">*</span>}
        </label>
        {field.help_text && <div className="form-preview-help">{frozenHelp(field)}</div>}
        <div className="form-preview-static">
          {submittedAnswer || <span className="text-muted">—</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="p-2">
      <ContainerHeader icon={faFileLines} iconSize="1x" className="info" dataCy="completed-form-readonly-header">
        Completed Form
      </ContainerHeader>

      <div className="mt-3 mb-3 d-flex align-items-center gap-2">
        <Button
          color="info"
          size="sm"
          outline
          onClick={() => navigate('/forms?tab=completed')}
          dataCy="completed-form-back-btn"
        >
          Back to Forms
        </Button>
        <AsyncActionIcon
          id="completed-form-download-pdf-btn"
          icon={faFilePdf}
          toolTip="Download PDF"
          onClick={handleGeneratePdf}
          disabled={isGenerating || !id}
          successTitle="Form PDF generation started"
          spinnerDataCy="completed-form-download-pdf-spinner"
        />
      </div>

      <div className="form-preview px-3">
        {fields.length === 0 && <p className="text-muted">This form has no fields.</p>}
        {fields.map(renderField)}
      </div>
    </div>
  );
};

export default CompletedFormReadOnly;
